import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

export const MIN_PASSWORD_LENGTH = 12;

const COMMON = [
  "password",
  "123456",
  "qwerty",
  "letmein",
  "admin",
  "welcome",
  "iloveyou",
  "pokemon",
  "yugioh",
  "onepiece",
];

export type Strength = "too_short" | "weak" | "fair" | "strong";

/**
 * A simple local estimate for the meter only — identity-service checks the real policy
 * (length, breached/common lists) and answers `password_too_weak`.
 */
export function passwordStrength(password: string): Strength {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return "too_short";
  }
  const lower = password.toLowerCase();
  if (COMMON.some((word) => lower.includes(word)) || /^(.)\1+$/.test(password)) {
    return "weak";
  }
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((re) => re.test(password)).length;
  const unique = new Set(password).size;
  if (password.length >= 16 && unique >= 8) {
    return "strong";
  }
  if (classes >= 3 && unique >= 8) {
    return "strong";
  }
  return classes >= 2 && unique >= 6 ? "fair" : "weak";
}

const BAR: Record<Strength, { width: string; color: string }> = {
  too_short: { width: "w-1/4", color: "bg-[#b91c1c]" },
  weak: { width: "w-2/4", color: "bg-[#b45309]" },
  fair: { width: "w-3/4", color: "bg-[#1d4ed8]" },
  strong: { width: "w-full", color: "bg-[#047857]" },
};

export function PasswordStrengthMeter({ password }: { password: string }) {
  const { t } = useTranslation("auth");
  const strength = passwordStrength(password);
  const text =
    strength === "too_short"
      ? t("strength.too_short", { count: MIN_PASSWORD_LENGTH - password.length })
      : t(`strength.${strength}`);
  return (
    <div className="flex flex-col gap-1" data-slot="password-strength" data-strength={strength}>
      <div className="h-2 w-full overflow-hidden rounded bg-muted" aria-hidden>
        <div className={cn("h-full transition-all", BAR[strength].width, BAR[strength].color)} />
      </div>
      <p className="text-xs" aria-live="polite">
        <span className="font-medium">{t("strength.label")}: </span>
        {password ? text : t("strength.rule")}
      </p>
    </div>
  );
}
